import { useReadQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { TruncateTypography } from "components/TruncateTypography";
import { TotalQuantityByTsdfQuery } from "generated-graphql/graphql";
import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";
import { useWasteOverviewState } from "../../WasteOverviewContext";
import { smallDonutProps, statusColors, tsdfChartColors } from "./constants";

type LegendValueItemProps = {
  id: string;
  title: string;
  amount: number;
  totalAmount: number;
  selectedTsdfId?: string | null | undefined;
  truncate?: boolean;
};

type ChartDataItem = {
  id: string;
  label: (location: "tooltip" | "legend" | "arc") => string;
  value: number;
  tsdfName: string;
  tsdfId: string;
};

type TsdfDataItem = {
  destinationFacilityId: string;
  destinationFacilityName: string;
  totalQuantityLbs: number;
};

type LegendDataItem = {
  color: string;
  value: ReactElement;
  tooltip: string;
  facilityId: string;
  key: string;
};

type ProcessedData = {
  chartData: ChartDataItem[];
  legendData: LegendDataItem[];
  topTsdfs: TsdfDataItem[];
};

const DEFAULT_VISIBLE = 5;

function formatAmount(amount: number) {
  return amount.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

function formatPercentage(percentage: number) {
  return percentage >= 1 ? percentage.toFixed(0) : "<1";
}

function LegendValueItem({
  id,
  title,
  amount,
  totalAmount,
  selectedTsdfId,
  truncate = false,
}: LegendValueItemProps) {
  const percentage = (amount / totalAmount) * 100;
  const formattedPercentage = formatPercentage(percentage);
  const formattedAmount = formatAmount(amount);
  const opacity = !selectedTsdfId || selectedTsdfId === id ? 1 : 0.3;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      gap={2}
    >
      {truncate ? (
        <TruncateTypography variant="body2" sx={{ opacity }}>
          {title}
        </TruncateTypography>
      ) : (
        <Typography variant="body2" noWrap sx={{ flexGrow: 1, mr: 1, opacity }}>
          {title}
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{ textAlign: "right", whiteSpace: "nowrap", opacity }}
      >
        {formattedAmount} lbs {amount !== 0 ? `(${formattedPercentage}%)` : ""}
      </Typography>
    </Box>
  );
}

function createChartDataItem(
  item: TsdfDataItem,
  totalAmount: number
): ChartDataItem {
  return {
    id: item.destinationFacilityId,
    label: (location: "tooltip" | "legend" | "arc") => {
      const percentage = (item.totalQuantityLbs / totalAmount) * 100;
      const formattedPercentage = formatPercentage(percentage);
      const formattedAmount = formatAmount(item.totalQuantityLbs);

      if (location === "tooltip") {
        return `${item.destinationFacilityName}: ${formattedAmount} lbs (${formattedPercentage}%)`;
      }
      return item.destinationFacilityName;
    },
    value: item.totalQuantityLbs,
    tsdfName: item.destinationFacilityName,
    tsdfId: item.destinationFacilityId,
  };
}

function createLegendDataItem(
  item: TsdfDataItem,
  totalAmount: number,
  chartDataIndex: number,
  selectedTsdfId: string | null | undefined
): LegendDataItem {
  const color = tsdfChartColors[chartDataIndex % tsdfChartColors.length];

  return {
    color,
    value: (
      <LegendValueItem
        id={item.destinationFacilityId}
        key={item.destinationFacilityId}
        title={item.destinationFacilityName}
        amount={item.totalQuantityLbs}
        totalAmount={totalAmount}
        selectedTsdfId={selectedTsdfId}
        truncate={true}
      />
    ),
    tooltip: item.destinationFacilityName,
    facilityId: item.destinationFacilityId,
    key: item.destinationFacilityId,
  };
}

function processTsdfData(
  queryData: TotalQuantityByTsdfQuery | undefined,
  showAll: boolean,
  selectedIndex: number | null
): ProcessedData {
  const rawData = queryData?.totalQuantityByTsdf?.items || [];

  if (rawData.length === 0) {
    return {
      chartData: [],
      legendData: [],
      topTsdfs: [],
    };
  }

  const totalAmount = queryData?.totalQuantityByTsdf?.totalQuantityLbs || 0;

  // If there is only one TSDF with 0 pounds, we still want the chart to be displayed
  if (totalAmount === 0 && rawData.length === 1) {
    return {
      chartData: [{ ...createChartDataItem(rawData[0], 0), value: 1 }],
      legendData: [],
      topTsdfs: [],
    };
  }

  const chartData: ChartDataItem[] = rawData.map((item) =>
    createChartDataItem(item, totalAmount)
  );

  // Show top 5 or all based on showAll state
  const legendDisplayLimit = showAll ? rawData.length : DEFAULT_VISIBLE;
  let legendEntries = rawData.slice(0, legendDisplayLimit);
  let selectedItem: ChartDataItem | undefined;

  // If not expanded and user clicked on an entry not in top 5, add it to legend
  if (!showAll && selectedIndex !== null) {
    selectedItem = chartData[selectedIndex];
    if (selectedItem && selectedItem.tsdfId !== "other") {
      // Check if selected entry is not in the top 5
      const isInTopFive = legendEntries.some(
        (entry) => entry.destinationFacilityId === selectedItem?.tsdfId
      );
      if (!isInTopFive) {
        // Find the selected entry in the full combinedEntries array
        const selectedEntry = rawData.find(
          (entry) => entry.destinationFacilityId === selectedItem?.tsdfId
        );
        if (selectedEntry) {
          legendEntries = [...legendEntries, selectedEntry];
        }
      }
    }
  }

  // Prepare legend data
  const legendData: LegendDataItem[] = legendEntries.map((item) => {
    // Find the color based on chart data position
    const chartDataIndex = chartData.findIndex(
      (chartItem) => chartItem.tsdfId === item.destinationFacilityId
    );
    return createLegendDataItem(
      item,
      totalAmount,
      chartDataIndex,
      selectedItem?.tsdfId
    );
  });

  return {
    chartData,
    legendData,
    topTsdfs: rawData,
  };
}

export default function TsdfChart() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();

  const { tsdfQuery } = useWasteOverviewState();
  const { data } = useReadQuery(tsdfQuery);

  const processedData = useMemo(
    () => processTsdfData(data, showAll, selectedIndex),
    [data, showAll, selectedIndex]
  );

  const handleExpandButtonClick = useCallback(() => {
    setShowAll(!showAll);
    setSelectedIndex(null);
  }, [showAll]);

  const { chartData, legendData, topTsdfs } = processedData;

  const handleItemClick = (item: DefaultizedPieValueType) => {
    const clickedIndex = chartData.findIndex((d) => d.id === item.id);
    setSelectedIndex(clickedIndex === selectedIndex ? null : clickedIndex);
  };

  const handleLegendItemClick = (item: DefaultizedPieValueType) => {
    // Find the corresponding chart data index
    const chartIndex = legendData.findIndex((d) => d.key === item.id);

    if (chartIndex === selectedIndex || chartIndex === -1) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(chartIndex);
    }
  };

  const shouldShowExpandButton = topTsdfs.length > DEFAULT_VISIBLE;

  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <DonutChart
      {...smallDonutProps}
      seriesData={
        chartData.length > 0
          ? chartData
          : [
              {
                id: "no-tsdf",
                value: 1,
                label: "No TSDFs to show",
                color: statusColors.NONE,
              },
            ]
      }
      selectedIndex={selectedIndex}
      onItemClick={(event, pieItemIdentifier, item) => handleItemClick(item)}
      onLegendItemClick={handleLegendItemClick}
      legendItems={
        chartData.length > 0
          ? legendData
          : [{ value: "🛢️ No TSDFs to show", color: "white" }]
      }
      colors={tsdfChartColors}
      legend={{
        position: isLg ? (isMd ? "right" : "below") : "right",
        direction: "column",
        showExpandButton: shouldShowExpandButton,
        onExpandButtonClick: handleExpandButtonClick,
        expandButtonText: showAll
          ? "Collapse to Top 5 TSDFs"
          : "Show All TSDFs",
        expandButtonIcon: showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />,
      }}
      legendSx={{ width: "100%" }}
      justifyContent={
        isSm ? "center" : chartData.length > 0 ? "start" : "center"
      }
      alignItems={isSm ? "center" : chartData.length > 0 ? "start" : "center"}
    />
  );
}
